import { useCallback, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useHistory, useLocation } from "react-router";
import { stringify } from "query-string";
import { mutateErrors } from "../../../utils";
import { useSchoolId } from "../../school/hooks/useSchoolId";
import { useLanguageCourseConfig } from "../modals/hooks/useLanguageCourseConfig";
import { ILanguageCourseListItem } from "../types";
import useLanguageCourseQueries from "./useLanguageCourseQueries";
import { replaceForURLOptions } from "../helpers/parseFilterToUrlOptions";
import langs from "langs";

export const useLanguageCoursesPage = (disableAutoFetch: boolean = false) => {
  const languageCourseWizardConfigs = useLanguageCourseConfig();
  const history = useHistory();
  const location = useLocation();
  const { getLanguagesWithCourses } = useLanguageCourseQueries();
  const [filter, setFilter] = useState({});
  const schoolId = useSchoolId();

  const { isLoading, isError, error, data, refetch } = useQuery<{
    data: ILanguageCourseListItem;
  }>(
    `languageCourseList${schoolId}?${stringify(filter)}`,
    () => {
      return getLanguagesWithCourses({
        schoolId,
        ...filter,
      });
    },
    {
      enabled: !disableAutoFetch,
    },
  );

  const applyFilter = useCallback(
    (filter) => {
      setFilter({
        courseType: filter.courseType?.id,
        housingType: filter.housingType?.id,
      });

      history.replace({
        pathname: location.pathname,
        search: "?filters=" + JSON.stringify(replaceForURLOptions(filter)),
      });
    },
    [setFilter],
  );

  let errorMessage;
  if (isError) {
    const { message } = mutateErrors(error);
    errorMessage = message;
  }

  const languages = useMemo(() => {
    return Object.entries(data?.data ?? {})
      .map(([key, value]) => ({
        id: key,
        name: langs.where(3, key)!.name,
        courses: value,
      }))
      .sort((a, b) => (a.id > b.id ? 1 : -1));
  }, [data?.data]);

  return {
    isError,
    languages,
    isLoading,
    applyFilter,
    errorMessage,
    languageCourseWizardConfigs,
    refetchLanguageCourses: refetch,
  };
};
